<script setup lang="ts">
export interface IdentityBlockProps {
  /**
   * URL of the banner image.
   */
  bannerUrl?: string;
  /**
   * Array of banners.
   */
  banners?: IModelPoster[];
  /**
   * URL of the logo image.
   */
  logoUrl?: string;
  /**
   * Title of the identity block.
   */
  title: string;
  /**
   * Total number of events.
   */
  totalEventsNumber?: number;
  /**
   *  Number of live events.
   */
  liveEventsNumber?: number;
  /**
   * Number of upcoming events.
   */
  upcomingEventsNumber?: number;
  /**
   * Indicates whether displaying amount of events or not
   */
  hasEventsNumber?: boolean;
  /**
   * Indicates date of identity (basically it used for league structure in format 2023/2024)
   */
  date?: string;
  /**
   * Indicates whether the user is following.
   */
  isFollowing?: boolean;
  /**
   * Indicates whether following is disabled.
   */
  isFollowingDisabled?: boolean;
  /**
   * Indicates whether the following button is displayed.
   */
  hasFollowingButton?: boolean;
  /**
   * URL of the Facebook profile.
   */
  facebookUrl?: string;
  /**
   * URL of the X profile.
   */
  xUrl?: string;
  /**
   * URL of the Instagram profile.
   */
  instagramUrl?: string;
  /**
   * URL of the website.
   */
  webUrl?: string;
  /**
   * URL of the YouTube profile.
   */
  youtubeUrl?: string;
}

const { t } = useI18n();
const color = useColorMode();
const props = withDefaults(defineProps<IdentityBlockProps>(), {
  totalEventsNumber: 0,
  liveEventsNumber: 0,
  upcomingEventsNumber: 0,
});
const emit = defineEmits(["follow"]);
const onFollowClick = () => emit("follow");
const isDark = computed(() => color.value === "dark");
const withSocialIcons = computed(() => props.facebookUrl || props.xUrl || props.webUrl || props.instagramUrl || props.youtubeUrl);
</script>

<template>
  <div class="flex flex-col">
    <div v-if="props.banners?.length" class="w-full h-full aspect-[4/1] lg:aspect-[25/6] overflow-hidden hidden lg:flex">
      <BaseSlider
        :slides="props.banners"
        has-autoplay
        has-controls
        has-indicator
      />
    </div>

    <div
      class="flex px-10 py-6 w-full gap-6  flex-col items-center bg-[var(--base-card-bgColor)]"
      :class="[!props.banners?.length ? 'flex-col min-h-[443px] justify-center' : 'justify-between min-h-40 lg:flex-row lg:gap-0']"
    >
      <div
        class="flex items-center gap-6"
        :class="[!props.banners?.length ? 'flex-col' : 'flex-col lg:flex-row']"
      >
        <BaseLogo
          class="relative z-10"
          :class="{ border: !isDark }"
          :src="props.logoUrl"
          :alt="props.title"
          :width="120"
          :height="120"
          no-background
          fit="contain"
          :icon-background="isDark ? '#2c374b' : '#ffffff'"
          with-initials
          :title="props.title"
        />
        <div class="flex items-center flex-col gap-4" :class="[!props.banners?.length ? 'items-center' : 'lg:items-start']">
          <div class="text-main font-bold text-lg sm:text-xl md:text-3xl">
            <span> {{ props.title }}</span>
          </div>

          <div v-if="props.date" class="text-secondary">
            <span> {{ props.date }} </span>
          </div>

          <div v-if="props.hasEventsNumber" class="flex gap-2 text-secondary font-medium">
            <div class="flex justify-start min-w-[5rem]">
              <span>{{ props.liveEventsNumber }} {{ t("labels.live") }}</span>
            </div>

            <div class="flex border-x px-4 border-[var(--base-header-menu-line-color)] min-w-[5rem]">
              <span>{{ props.upcomingEventsNumber }} {{ t("labels.upcoming") }}</span>
            </div>

            <div class="flex justify-end min-w-[5rem]">
              <span>{{ props.totalEventsNumber }} {{ t("labels.total") }}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex flex-wrap justify-end items-center gap-x-10 gap-y-5 text-[var(--base-icon-color)]"
        :class="[!props.banners?.length ? 'flex-col' : 'flex-col lg:flex-row']"
      >
        <BaseSocialIcons
          v-if="withSocialIcons"
          :facebook-url="props.facebookUrl"
          :x-url="props.xUrl"
          :instagram-url="props.instagramUrl"
          :youtube-url="props.youtubeUrl"
          :web-url="props.webUrl"
        />
        <BaseButton
          v-if="props.hasFollowingButton"
          class="text-button-bold w-[12rem]"
          :type="props.isFollowing ? 'secondary' : 'primary'"
          name="follow-club"
          :disabled="props.isFollowingDisabled"
          @click.prevent="onFollowClick()"
        >
          {{ props.isFollowing ? t("labels.following") : t("labels.follow") }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>
